import type {
  CSSProperties,
  PropsWithChildren,
  ReactNode,
  RefObject,
} from 'react';
import { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import {
  breakpointMediumStart,
  borderRadius,
  neutral0,
  spacing4,
  breakpointSmallEnd,
  spacing6,
} from '../../design-tokens';
import { breakpointMedium, CloseButton } from 'lib/design-system';
import { columnWidth } from 'lib/design-system/mixins';

export type ModalSizes = 'small' | 'medium' | 'large';

const modalDesktopColumn = {
  small: 4,
  medium: 6,
  large: 8,
};

const modalTabletColumn = {
  small: 6,
  medium: 8,
  large: 10,
};

const Dialog = styled.dialog<
  {
    children?: ReactNode;
    className?: string;
    ref: RefObject<HTMLDialogElement | null>;
    style?: CSSProperties;
  } & {
    fullScreenOnMobile: boolean;
    size: ModalSizes;
    height?: number;
    noPadding?: boolean;
    disableAnimation?: boolean;
    centered?: boolean;
  }
>`
  ${props =>
    props.height
      ? css`
          height: ${props.height}px;
        `
      : css`
          @media (min-width: ${breakpointMedium.start}) {
            height: revert;
            max-block-size: revert;
          }
        `}
  ${props =>
    props.fullScreenOnMobile
      ? css`
          @media (max-width: ${breakpointSmallEnd}) {
            height: 100%;
            max-block-size: 100vh;
          }
        `
      : css`
          position: relative;
        `}

  display: flex;
  flex-direction: column;
  min-height: 0;
  max-width: 100%;
  max-height: 100%;
  ${props =>
    !props.noPadding &&
    css`
      padding: 44px ${spacing4} ${spacing6};
    `}
  box-sizing: border-box;
  border: none;
  border-top-left-radius: ${borderRadius(2)};
  border-top-right-radius: ${borderRadius(2)};
  background: ${neutral0};

  ${props =>
    props.centered
      ? css`
          border-bottom-left-radius: ${borderRadius(2)};
          border-bottom-right-radius: ${borderRadius(2)};
        `
      : css`
          inset-block-start: auto;
        `}

  * {
    box-sizing: border-box;
  }

  ${props =>
    columnWidth(
      4,
      modalTabletColumn[props.size],
      modalDesktopColumn[props.size],
    )}

  ${CloseButton} {
    position: absolute;
    top: 16px;
    right: 24px;
  }

  ${props =>
    !props.disableAnimation &&
    css`
      @media (prefers-reduced-motion: no-preference) {
        animation: slideUp 0.4s;
      }
    `}

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }

    to {
      transform: translateY(0);
    }
  }

  @media (min-width: ${breakpointMediumStart}) {
    ${props =>
      !props.noPadding &&
      css`
        padding: 44px ${spacing6} ${spacing6};
      `}
    border-radius: ${borderRadius(2)};
    inset-block-start: 0px;
    inset-block-end: 0px;
    min-width: auto;
  }

  ::backdrop {
    background: rgba(25, 25, 25, 0.5);
  }

  div > {
    display: flex;
    flex-direction: column;
  }
`;

type ModalProps = PropsWithChildren<{
  fullScreenOnMobile?: boolean;
  name: string;
  onDismiss: () => void;
  className?: string;
  size: ModalSizes;
  style?: CSSProperties;
  height?: number;
  hideCloseButton?: boolean;
  noPadding?: boolean;
  closeOnClickOutside?: boolean;
  disableAnimation?: boolean;
  centered?: boolean;
}>;

export const Modal = ({
  fullScreenOnMobile = false,
  name,
  children,
  onDismiss,
  className,
  size,
  style,
  height,
  hideCloseButton,
  noPadding,
  closeOnClickOutside,
  disableAnimation,
  centered,
}: ModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    document.body.style.top = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';

    return () => {
      document.body.style.top = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);

  useEffect(() => {
    const currentRef = dialogRef.current;

    if (!currentRef) {
      return;
    }

    currentRef.addEventListener('close', onDismiss);

    if (!currentRef.open) {
      currentRef.showModal();
    }

    return () => {
      currentRef.removeEventListener('close', onDismiss);
    };
  }, [onDismiss, dialogRef]);

  useEffect(() => {
    const currentRef = dialogRef.current;

    if (!currentRef || !closeOnClickOutside) {
      return;
    }

    function handleClickOutside(event: PointerEvent) {
      if (currentRef) {
        const eventX = event.x;
        const eventY = event.y;
        const refRect = currentRef.getBoundingClientRect();
        const pointerType = event.pointerType;

        if (
          pointerType !== '' &&
          (eventX < refRect.x ||
            eventY < refRect.y ||
            eventX > refRect.x + refRect.width ||
            eventY > refRect.y + refRect.height)
        ) {
          onDismiss();
        }
      }
    }

    currentRef.addEventListener('pointerdown', handleClickOutside);

    return () => {
      currentRef.removeEventListener('pointerdown', handleClickOutside);
    };
  }, [dialogRef, onDismiss, closeOnClickOutside]);

  useEffect(() => {
    const currentRef = dialogRef.current;

    if (!currentRef) {
      return;
    }

    function handleKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (closeOnClickOutside) return onDismiss();
        event.preventDefault();
      }
    }

    currentRef.addEventListener('keydown', handleKeydown);

    return () => {
      currentRef.removeEventListener('keydown', handleKeydown);
    };
  }, [dialogRef, onDismiss, closeOnClickOutside]);

  return (
    <Dialog
      fullScreenOnMobile={fullScreenOnMobile}
      size={size}
      aria-label={name}
      ref={dialogRef}
      className={className}
      style={style}
      height={height}
      noPadding={noPadding}
      disableAnimation={disableAnimation}
      centered={centered}
    >
      {!hideCloseButton && <CloseButton onClick={onDismiss} />}
      {children}
    </Dialog>
  );
};
